import { useMemo } from 'react';
import { useStore } from 'effector-react';
import { nanoid } from 'nanoid';
import { AVATAR_DOMAIN } from '@constants';
import { CheckboxNoFunc } from '@components/Checkbox';
import TextLink from '@components/TextLink';
import type {
  SeatData,
  SeatDepartmentData,
  SeatItem,
  SeatMemberData,
} from '@modules/header/ShareConfigModal/types';
import IconClose from '@components/icons/IconClose';
import cn from '@utils/cn';
import {
  $seatsSelected,
  $seatsSelectedApi,
  $seatsSpcialIds,
} from './seatsStores';
import { useDepartmentMembers, useSeatMembers } from './seatsApis';
import ImgDept from './img-dept.png';

export function RootItem({
  onNextClick,
}: {
  onNextClick: (payload: {
    id: number;
    name: string;
    members: SeatData['seatMembers'];
    depts: SeatData['departments'];
  }) => void;
}) {
  const selected = useStore($seatsSelected);
  const { data, isLoading } = useSeatMembers();

  const checked = useMemo(
    () =>
      !!selected.find((x) => x.accessScope === 0 && x.relationId === data?.id),
    [selected, data?.id]
  );

  if (isLoading || !data) return null;

  const handleClick = () => {
    if (checked) {
      $seatsSelectedApi.remove({
        relationId: data.id,
        accessScope: 0,
      });
    } else {
      $seatsSelectedApi.add({
        id: nanoid(),
        accessScope: 0,
        relationId: data.id,
        relationName: data.name,
        relationAvatar: data.avatar,
      });
    }
  };

  return (
    <div
      className="relative flex h-55 select-none items-center rounded pl-10 pr-65 hover:bg-white-light"
      onClick={handleClick}
    >
      <CheckboxNoFunc checked={checked} className="mr-10 shrink-0" />
      <Cover src={`${AVATAR_DOMAIN}${data.avatar}`} />
      <Content title={data.name} subTitle="全体成员" />
      {data.departments.length + data.seatMembers.length > 0 && (
        <NextLevel
          disabled={checked}
          onClick={() => {
            onNextClick({
              id: data.id,
              name: data.name,
              members: data.seatMembers,
              depts: data.departments,
            });
          }}
        />
      )}
    </div>
  );
}

export function DeptItem({
  data,
  onNextClick,
}: {
  data: SeatData['departments'][number];
  onNextClick: (payload: {
    id: number;
    name: string;
    members: SeatDepartmentData['seatMembers'];
    depts: SeatData['departments'];
  }) => void;
}) {
  const selected = useStore($seatsSelected);
  const { data: respData, isLoading } = useDepartmentMembers(data.id);

  const checked = useMemo(
    () =>
      !!selected.find((x) => x.accessScope === 1 && x.relationId === data.id),
    [selected, data.id]
  );

  const handleClick = () => {
    if (checked) {
      $seatsSelectedApi.remove({
        relationId: data.id,
        accessScope: 1,
      });
    } else {
      $seatsSelectedApi.add({
        id: nanoid(),
        accessScope: 1,
        relationId: data.id,
        relationName: data.name,
        relationAvatar: '',
      });
    }
  };

  return (
    <div
      className=" relative flex h-55 select-none items-center rounded pl-10 pr-65 hover:bg-white-light"
      onClick={handleClick}
    >
      <CheckboxNoFunc checked={checked} className="mr-10 shrink-0" />
      <Cover src={ImgDept} />
      <Content title={data.name} />
      {!isLoading && !!respData?.seatMembers.length && (
        <NextLevel
          disabled={checked}
          onClick={() => {
            onNextClick({
              id: data.id,
              name: data.name,
              members: respData.seatMembers,
              depts: respData.departments,
            });
          }}
        />
      )}
    </div>
  );
}

export function MemberItem({ data }: { data: SeatMemberData }) {
  const selected = useStore($seatsSelected);
  const { operatorId, ownerId } = useStore($seatsSpcialIds);

  const checked = useMemo(
    () =>
      !!selected.find(
        (x) => x.accessScope === 2 && x.relationId === data.memberId
      ),
    [selected, data.memberId]
  );

  const role: string | undefined = useMemo(() => {
    if (data.memberId === ownerId) return '企业所有者';
    if (data.memberId === operatorId) return '作品归属人';
  }, [operatorId, ownerId, data.memberId]);

  const handleClick = () => {
    if (role) return;
    if (checked) {
      $seatsSelectedApi.remove({
        relationId: data.memberId,
        accessScope: 2,
      });
    } else {
      $seatsSelectedApi.add({
        id: nanoid(),
        accessScope: 2,
        relationId: data.memberId,
        relationName: data.memberName,
        relationAvatar: data.memberAvatar,
        relationMobile: data.memberMobile,
      });
    }
  };

  return (
    <div
      className="relative flex h-55 select-none items-center rounded pl-10 pr-65 hover:bg-white-light"
      onClick={handleClick}
    >
      <CheckboxNoFunc
        checked={role ? true : checked}
        className={cn('mr-10 shrink-0', {
          'opacity-50': role,
        })}
      />
      <Cover src={`${AVATAR_DOMAIN}${data.memberAvatar}`} />
      <Content
        title={data.memberName}
        memberRole={role}
        subTitle={data.memberMobile}
      />
    </div>
  );
}

export function SelectedItem({ data }: { data: SeatItem }) {
  const { accessScope, relationAvatar, relationId, relationName } = data;

  const getSrc = () => {
    if (accessScope === 1) {
      return ImgDept;
    }
    return `${AVATAR_DOMAIN}${relationAvatar}`;
  };

  const getSubTitle = () => {
    if (accessScope === 0) {
      return '全体成员';
    }
    if (accessScope === 2) {
      return data.relationMobile;
    }
    return undefined;
  };
  return (
    <div className="flex h-55 select-none items-center rounded px-10 hover:bg-white-light">
      <Cover src={getSrc()} />
      <Content title={relationName} subTitle={getSubTitle()} />
      <IconClose
        scheme="light"
        onClick={() => {
          $seatsSelectedApi.remove({
            relationId,
            accessScope,
          });
        }}
      />
    </div>
  );
}

function Cover({ src }: { src: string }) {
  return (
    <img alt="" src={src} draggable={false} className="h-32 w-32 rounded" />
  );
}

function Content({
  title,
  memberRole,
  subTitle,
}: {
  title: string;
  memberRole?: string;
  subTitle?: string;
}) {
  return (
    <div className="ml-10 flex min-w-0 grow flex-col">
      {memberRole ? (
        <div className="flex w-full min-w-0">
          <div className="truncate" title={title}>
            {title}
          </div>
          <div className="shrink-0">{`（${memberRole}）`}</div>
        </div>
      ) : (
        <div className="truncate" title={title}>
          {title}
        </div>
      )}
      {!!subTitle && <div className="text-sm">{subTitle}</div>}
    </div>
  );
}

function NextLevel({
  disabled,
  onClick,
}: {
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <div
      className="absolute right-10 w-45 shrink-0 text-right"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <TextLink onClick={onClick} disabled={disabled}>
        {'下级 >'}
      </TextLink>
    </div>
  );
}
